import { useState } from 'react'
import { transparentize } from 'polished'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { NegativeIcon, PlayIcon } from '@shipt/design-system-icons'
import { PromoAssetBanner } from '@shipt/design-system-banner'
import { IconButton } from '@shipt/design-system-buttons'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { Body, Display } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type VideoBannerData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { DynamicVideoBannerModal } from '@/features/cms/components/CMS/components/Modals/VideoBanner/DynamicVideoBannerModal'
import { type VideoTimeSpent } from '@/features/cms/components/CMS/components/Modals/VideoBanner'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import { spacing } from '@/features/shared/theme/tokens'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '@/features/shared/utils/sessionStorage'
import { VIDEO_BANNER_DISMISSED_SESSION_STORAGE } from '@/features/cms/components/CMS/components/VideoBanner/constants'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { theme } from '@/features/shared/constants/shiptTheme'

export const VideoBanner = ({ content_type_id, id, data }: VideoBannerData) => {
  const [isBannerDismissed, setIsBannerDismissed] = useState(
    () => getSessionStorageItem(VIDEO_BANNER_DISMISSED_SESSION_STORAGE) === id,
  )
  const { openDialog } = useHandleDialog()
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()
  const { screenSm: isMobile } = useMediaQuery()

  const isBanner = data.variant === 'banner'
  const videoImpressionContent = isBanner
    ? 'watch_and_earn_video_banner'
    : 'watch_and_earn_video_hero'

  const trackVideoProgress = (timeSpent: VideoTimeSpent) => {
    trackCMSElementViewed({
      type: 'impression',
      content: timeSpent,
      message_goal: 'watch_and_earn_video',
      display_sublocation: urlPathnameToLocationName(),
    })
  }

  const trackVideoBannerViewed = () => {
    if (isBanner && isBannerDismissed) return
    trackCMSElementViewed({
      content: videoImpressionContent,
      type: 'video',
    })
  }

  const openVideoModal = () => {
    trackCMSElementClicked({
      content: 'video start',
      message_goal: 'watch_and_earn_video',
      type: 'button',
    })

    openDialog(DynamicVideoBannerModal, {
      title: isBanner ? (data.banner_heading ?? '') : (data.thumbnail_heading ?? ''),
      videoSrc: (isMobile && data.mobile_video?.src) || data.video.src,
      closedCaptionSrc: data.video_closed_captioning?.src,
      onVideoPause: () => {
        trackCMSElementClicked({
          content: 'video pause',
          message_goal: 'watch_and_earn_video',
          type: 'button',
        })
      },
      onVideoProgress: trackVideoProgress,
    })
  }

  const dismissBanner = () => {
    setSessionStorageItem(VIDEO_BANNER_DISMISSED_SESSION_STORAGE, id)
    setIsBannerDismissed(true)
  }

  try {
    return (
      <VideoBannerContainer
        data-variant={data.variant}
        {...getCMSWrapperProps({ content_type_id, id })}
      >
        <ScrollIntoView
          className={isBanner ? 'fullWidth withContent' : 'fullWidth contentAtLgMobile'}
          onInView={trackVideoBannerViewed}
        >
          {isBanner ? (
            !isBannerDismissed && (
              <BannerFrame>
                <BannerButton
                  aria-label={`Play Video: ${
                    data.banner_heading || data.mobile_thumbnail.alt || 'Video banner'
                  }`}
                  onClick={openVideoModal}
                  type="button"
                >
                  <BannerPromoAssetBanner
                    title={data.banner_heading ?? ''}
                    description={data.banner_subheading ?? data.thumbnail_subheading}
                    asset={
                      <>
                        <BannerAsset align="center" aria-hidden justify="center">
                          <Image
                            alt=""
                            src={data.thumbnail.src}
                            fill
                            sizes="80px"
                            style={thumbnailImageStyle}
                          />
                          <PlayBadge align="center" data-size="sm" justify="center">
                            <PlayIcon size="sm" />
                          </PlayBadge>
                        </BannerAsset>
                        <MobileBannerAsset align="center" aria-hidden justify="center">
                          <Image
                            alt=""
                            src={data.mobile_thumbnail.src}
                            fill
                            sizes="61px"
                            style={thumbnailImageStyle}
                          />
                          <PlayBadge align="center" data-size="sm" justify="center">
                            <PlayIcon size="sm" />
                          </PlayBadge>
                        </MobileBannerAsset>
                      </>
                    }
                    variant="brand"
                    size="sm"
                    type="leading"
                    hasEmphasis
                  />
                </BannerButton>
                <DismissButton
                  aria-label="Dismiss video banner"
                  icon={NegativeIcon}
                  onClick={dismissBanner}
                  surface="inverse"
                  variant="ghost"
                  size={{ base: 'md', md: 'lg' }}
                />
              </BannerFrame>
            )
          ) : (
            <HeroButton
              aria-label={`Play Video: ${
                data.mobile_thumbnail.alt || data.thumbnail.alt || 'Video banner'
              }`}
              onClick={openVideoModal}
              type="button"
            >
              <Box display={{ base: 'none', md: 'block' }}>
                <Image
                  alt={data.thumbnail.alt || data.thumbnail_heading || ''}
                  src={data.thumbnail.src}
                  fill
                  sizes="100vw"
                  style={thumbnailImageStyle}
                />
              </Box>
              <Box display={{ base: 'block', md: 'none' }}>
                <Image
                  alt={data.mobile_thumbnail.alt || data.thumbnail_heading || ''}
                  src={data.mobile_thumbnail.src}
                  fill
                  sizes="100vw"
                  style={thumbnailImageStyle}
                />
              </Box>
              <HeroContent align="center" justify="center" padding={['lg']} spacing="lg">
                <PlayBadge align="center" aria-hidden data-size="lg" justify="center">
                  <PlayIcon size="xl" />
                </PlayBadge>
                {!!data.thumbnail_heading && (
                  <Display size="xl" surface="inverse">
                    {data.thumbnail_heading}
                  </Display>
                )}
                {!!data.thumbnail_subheading && (
                  <Body size={{ base: 'lg', md: 'xxl' }} surface="inverse">
                    {data.thumbnail_subheading}
                  </Body>
                )}
              </HeroContent>
            </HeroButton>
          )}
        </ScrollIntoView>
      </VideoBannerContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const thumbnailImageStyle = { objectFit: 'cover' } as const

const VideoBannerContainer = styled(GridLayoutContainer)`
  &[data-variant='banner'] {
    @media ${screenSizes.max_mobile} {
      margin-bottom: ${spacing('lg')};
    }

    /* Full-bleed black background while the content stays in the content column */
    & > .fullWidth.withContent {
      background-color: ${theme.color.background.base.neutral.inverse};
    }
  }

  &[data-variant='video-only'] {
    --content-max-width: 1440px;
  }
`

const mediaOverlay = css`
  &::before {
    content: '';
    inset: 0;
    position: absolute;
    z-index: 1;
    background: ${transparentize(0.4, theme.color.background.base.neutral.inverse)};
  }

  [data-variant='banner'] &::before {
    background: ${transparentize(0.8, theme.color.background.base.neutral.inverse)};
  }
`

const BannerButton = styled(TransparentButton)`
  color: inherit;
  display: block;
  text-align: inherit;
  width: 100%;
`

const BannerFrame = styled.div`
  position: relative;
`

const BannerPromoAssetBanner = styled(PromoAssetBanner)`
  padding-right: 56px;

  /* Let the full-bleed wrapper provide the background; keep text/button inverse */
  && {
    background-color: transparent;
    border-color: transparent;
  }

  /* On mWeb, align content to the 16px grid gutter (drop the banner's own inline-left padding) */
  @media ${screenSizes.max_mobile} {
    && {
      padding-left: 0;
    }
  }
`

const DismissButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`

const BannerAsset = styled(Row)`
  border-radius: 4px;
  display: none;
  height: 48px;
  overflow: hidden;
  position: relative;
  width: 80px;

  @media ${screenSizes.tablet} {
    display: flex;
  }

  ${mediaOverlay}
`

const MobileBannerAsset = styled(BannerAsset)`
  display: flex;
  height: 87px;
  width: 61px;

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const PlayBadge = styled(Row)`
  background-color: ${theme.color.background.base.neutral.subtle_00};
  border-radius: 50%;
  position: relative;
  z-index: 2;

  &[data-size='sm'] {
    color: ${theme.color.content.base.neutral.primary};
    height: 24px;
    width: 24px;
  }

  &[data-size='lg'] {
    height: 56px;
    width: 56px;
  }
`

const HeroButton = styled(BannerButton)`
  border-radius: 0;
  height: 627px;
  overflow: hidden;
  position: relative;

  ${mediaOverlay}

  @media ${screenSizes.lgMobile} {
    border-radius: 24px;
  }
`

const HeroContent = styled(Column)`
  inset: 0;
  position: absolute;
  text-align: center;
  z-index: 2;
`
